import { useState, useEffect, useCallback, useMemo } from 'react';
import StorageService from '../services/storageService';
import { KEYS } from '../services/initService';
import { DEFAULT_HERO_IMAGES } from '../data/heroImages';
import { mergeBusiness } from '../data/businessDefaults';
import { useBusinessSettings } from '../context/BusinessSettingsContext';
import AdminMenuItemCard from './admin/AdminMenuItemCard';
import AdminImagePicker from './admin/AdminImagePicker';

const TABS = ['Overview', 'Business', 'Menu', 'Hero', 'Orders', 'Reviews'];

function localDateKey(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function buildSalesRows(orders, days = 14) {
  const rows = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  for (let i = days - 1; i >= 0; i -= 1) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    const key = localDateKey(d);
    rows.push({ dateKey: key, date: d, total: 0, count: 0 });
  }
  const indexByKey = Object.fromEntries(rows.map((r, idx) => [r.dateKey, idx]));
  orders.forEach((o) => {
    const key = localDateKey(new Date(o.createdAt));
    const idx = indexByKey[key];
    if (idx !== undefined) {
      rows[idx].total += o.total || 0;
      rows[idx].count += 1;
    }
  });
  const max = Math.max(...rows.map((r) => r.total), 1);
  return { rows, max };
}

export default function AdminDashboard({ onExit }) {
  const { formatPrice, refresh: refreshBusinessContext } = useBusinessSettings();
  const [businessDraft, setBusinessDraft] = useState(() => mergeBusiness(null));
  const [businessSaveMsg, setBusinessSaveMsg] = useState('');
  const [loggedIn, setLoggedIn] = useState(false);
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [tab, setTab] = useState('Overview');
  const [menuItems, setMenuItems] = useState([]);
  const [orders, setOrders] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [heroImages, setHeroImages] = useState(DEFAULT_HERO_IMAGES);
  const [dragIndex, setDragIndex] = useState(null);
  const [dbHealthy, setDbHealthy] = useState(null);
  const [showTodayOnlyOrders, setShowTodayOnlyOrders] = useState(false);
  const [visibleOrderStatuses, setVisibleOrderStatuses] = useState(['new', 'preparing', 'ready', 'collected']);
  const [newItem, setNewItem] = useState({
    name: '',
    desc: '',
    category: 'Noodles',
    price: '',
    emoji: '🍽',
    prepMinutes: 12,
    spiceLevel: 1,
    imageUrl: '',
    stockUnits: '',
  });
  const [newImageFile, setNewImageFile] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    const [m, o, r] = await Promise.all([
      StorageService.getAll(KEYS.MENU),
      StorageService.getAll(KEYS.ORDERS),
      StorageService.getAll(KEYS.REVIEWS),
    ]);
    const heroStored = await StorageService.get(KEYS.HERO_IMAGES);
    const heroFromStore =
      Array.isArray(heroStored) && heroStored.length > 0
        ? heroStored.slice(0, 5).map((img, idx) => ({
          url: String(img?.url || '').trim(),
          label: String(img?.label || DEFAULT_HERO_IMAGES[idx]?.label || `Hero ${idx + 1}`).trim(),
        }))
        : DEFAULT_HERO_IMAGES;
    const bizRaw = await StorageService.get(KEYS.BUSINESS);
    setBusinessDraft(mergeBusiness(bizRaw));
    setMenuItems(m);
    setOrders(o);
    setReviews(r);
    setHeroImages(heroFromStore);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (loggedIn) loadData();
  }, [loggedIn, loadData, tab]);

  useEffect(() => {
    if (!loggedIn) return undefined;
    let mounted = true;
    const checkHealth = async () => {
      try {
        const res = await fetch('/api/health', { credentials: 'omit' });
        if (!mounted) return;
        setDbHealthy(res.ok);
      } catch {
        if (!mounted) return;
        setDbHealthy(false);
      }
    };
    checkHealth();
    const timer = setInterval(checkHealth, 30000);
    return () => {
      mounted = false;
      clearInterval(timer);
    };
  }, [loggedIn]);

  const { rows: salesRows, max: salesMax } = useMemo(
    () => buildSalesRows(orders, 14),
    [orders]
  );
  const categoryOptions = useMemo(
    () => Array.from(new Set(menuItems.map((i) => String(i.category || '').trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b)),
    [menuItems]
  );

  const todayKey = localDateKey(new Date());
  const todaySales = salesRows.find((r) => r.dateKey === todayKey);
  const totalRevenueAllTime = useMemo(
    () => orders.reduce((s, o) => s + (o.total || 0), 0),
    [orders]
  );
  const filteredOrders = useMemo(() => {
    const byStatus = orders.filter((order) => visibleOrderStatuses.includes(order.status || 'new'));
    if (!showTodayOnlyOrders) return byStatus;
    const now = new Date();
    return byStatus.filter((order) => {
      const dt = new Date(order.pickupSlot || order.createdAt);
      return (
        dt.getFullYear() === now.getFullYear()
        && dt.getMonth() === now.getMonth()
        && dt.getDate() === now.getDate()
      );
    });
  }, [orders, showTodayOnlyOrders, visibleOrderStatuses]);
  const groupedOrdersByPickup = useMemo(() => {
    const sorted = [...filteredOrders].sort((a, b) => {
      const ta = new Date(a.pickupSlot || a.createdAt).getTime();
      const tb = new Date(b.pickupSlot || b.createdAt).getTime();
      return ta - tb;
    });
    const groups = new Map();
    sorted.forEach((order) => {
      const key = order.pickupLabel || 'ASAP';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(order);
    });
    return Array.from(groups.entries()).map(([timeLabel, list]) => ({ timeLabel, list }));
  }, [filteredOrders]);

  const handleLogin = async () => {
    const stored = await StorageService.get(KEYS.ADMIN_PASS);
    if (password === stored) {
      setLoggedIn(true);
      setLoginError('');
    } else {
      setLoginError('Incorrect password. Demo: admin2025');
    }
  };

  const toggleField = async (id, field) => {
    const item = menuItems.find((i) => i.id === id);
    if (!item) return;
    const updated = await StorageService.update(KEYS.MENU, id, { [field]: !item[field] });
    setMenuItems((prev) => prev.map((i) => (i.id === id ? updated : i)));
  };

  const updateStock = async (id, raw) => {
    const v = raw === '' ? null : Math.max(0, Number(raw));
    if (raw !== '' && Number.isNaN(v)) return;
    const updated = await StorageService.update(KEYS.MENU, id, { stockUnits: v });
    if (updated) setMenuItems((prev) => prev.map((i) => (i.id === id ? updated : i)));
  };

  const updateCategory = async (id, rawCategory) => {
    const category = rawCategory.trim();
    if (!category) return;
    const updated = await StorageService.update(KEYS.MENU, id, { category });
    if (updated) setMenuItems((prev) => prev.map((i) => (i.id === id ? updated : i)));
  };

  const updateImageUrl = async (id, url) => {
    const trimmed = url.trim();
    const updated = await StorageService.update(KEYS.MENU, id, {
      image: trimmed === '' ? null : trimmed,
    });
    if (updated) setMenuItems((prev) => prev.map((i) => (i.id === id ? updated : i)));
  };

  const updateImageFile = (id, file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      const updated = await StorageService.update(KEYS.MENU, id, { image: reader.result });
      if (updated) setMenuItems((prev) => prev.map((i) => (i.id === id ? updated : i)));
    };
    reader.readAsDataURL(file);
  };

  const deleteMenuItem = async (id) => {
    if (!window.confirm('Delete this menu item permanently?')) return;
    await StorageService.delete(KEYS.MENU, id);
    setMenuItems((prev) => prev.filter((i) => i.id !== id));
  };

  const handleAddItem = async () => {
    if (!newItem.name.trim() || !newItem.price) return;
    const price = Number(newItem.price);
    if (Number.isNaN(price) || price < 0) return;
    const image = newImageFile || (newItem.imageUrl.trim() || null);
    const stockRaw = newItem.stockUnits.trim();
    const stockUnits = stockRaw === '' ? null : Math.max(0, Number(stockRaw));
    const item = {
      id: `m${Date.now()}`,
      name: newItem.name.trim(),
      desc: newItem.desc.trim() || 'Fresh Thai flavour, made to order.',
      category: newItem.category.trim() || 'Uncategorized',
      price,
      emoji: newItem.emoji.trim() || '🍽',
      image,
      tags: [],
      active: true,
      soldOut: false,
      featured: false,
      spiceLevel: Number(newItem.spiceLevel) || 0,
      prepMinutes: Number(newItem.prepMinutes) || 10,
      stockUnits,
    };
    await StorageService.push(KEYS.MENU, item);
    setMenuItems((prev) => [...prev, item]);
    setNewItem({
      name: '',
      desc: '',
      category: categoryOptions[0] || 'Noodles',
      price: '',
      emoji: '🍽',
      prepMinutes: 12,
      spiceLevel: 1,
      imageUrl: '',
      stockUnits: '',
    });
    setNewImageFile(null);
    setShowAddForm(false);
  };

  const updateOrderStatus = async (orderId, status) => {
    const updated = await StorageService.update(KEYS.ORDERS, orderId, { status });
    setOrders((prev) => prev.map((o) => (o.id === orderId ? updated : o)));
  };

  const printOrderLabel = (order) => {
    const labelWindow = window.open('', '_blank', 'width=420,height=340');
    if (!labelWindow) return;
    const safeName = String(order.name || 'Guest');
    const safeOrderId = String(order.orderId || order.id || 'N/A');
    const safePickup = String(order.pickupLabel || 'ASAP');
    labelWindow.document.write(`
      <!doctype html>
      <html>
        <head>
          <title>Order Label ${safeOrderId}</title>
          <meta charset="utf-8" />
          <link rel="preconnect" href="https://fonts.googleapis.com" />
          <link href="https://fonts.googleapis.com/css2?family=Noto+Sans:wght@400;700;800&display=swap" rel="stylesheet" />
          <style>
            body {
              margin: 0;
              padding: 18px;
              font-family: "Noto Sans", system-ui, -apple-system, "Segoe UI", sans-serif;
              background: #fff;
              color: #111;
            }
            .label {
              width: 320px;
              border: 2px solid #111;
              border-radius: 12px;
              padding: 14px;
            }
            .brand {
              font-size: 12px;
              text-transform: uppercase;
              letter-spacing: .08em;
              margin-bottom: 10px;
              color: #333;
              font-weight: 700;
            }
            .row { margin-bottom: 10px; }
            .k { font-size: 12px; text-transform: uppercase; letter-spacing: .07em; color: #666; }
            .v { font-size: 20px; font-weight: 800; line-height: 1.2; color: #111; }
            .v-small { font-size: 17px; font-weight: 700; line-height: 1.25; color: #111; }
            @media print {
              body { padding: 0; }
              .label { border-width: 1.5px; border-radius: 0; width: 100%; box-sizing: border-box; }
            }
          </style>
        </head>
        <body>
          <div class="label">
            <div class="brand">Thai Express - Order Label</div>
            <div class="row">
              <div class="k">Customer</div>
              <div class="v-small">${safeName}</div>
            </div>
            <div class="row">
              <div class="k">Order Number</div>
              <div class="v">${safeOrderId}</div>
            </div>
            <div class="row">
              <div class="k">Pickup Time</div>
              <div class="v-small">${safePickup}</div>
            </div>
          </div>
          <script>
            window.onload = function () {
              window.print();
              window.onafterprint = function () { window.close(); };
            };
          </script>
        </body>
      </html>
    `);
    labelWindow.document.close();
  };

  const deleteReview = async (id) => {
    if (!window.confirm('Delete this review?')) return;
    await StorageService.delete(KEYS.REVIEWS, id);
    setReviews((prev) => prev.filter((r) => r.id !== id));
  };

  const saveBusinessSettings = async () => {
    setBusinessSaveMsg('');
    const merged = mergeBusiness(businessDraft);
    try {
      await StorageService.set(KEYS.BUSINESS, merged);
      setBusinessDraft(merged);
      await refreshBusinessContext();
      setBusinessSaveMsg('Saved.');
      window.setTimeout(() => setBusinessSaveMsg(''), 2800);
    } catch (err) {
      console.error(err);
      setBusinessSaveMsg('Could not save. Try again.');
    }
  };

  const persistHeroImages = async (nextImages) => {
    await StorageService.set(KEYS.HERO_IMAGES, nextImages);
    setHeroImages(nextImages);
  };

  const updateHeroLabel = async (index, value) => {
    const next = heroImages.map((img, idx) => (idx === index ? { ...img, label: value } : img));
    await persistHeroImages(next);
  };

  const updateHeroUrl = async (index, value) => {
    const trimmed = value.trim();
    const next = heroImages.map((img, idx) => (idx === index ? { ...img, url: trimmed } : img));
    await persistHeroImages(next);
  };

  const updateHeroFile = (index, file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      const next = heroImages.map((img, idx) => (idx === index ? { ...img, url: reader.result } : img));
      await persistHeroImages(next);
    };
    reader.readAsDataURL(file);
  };

  const resetHeroImage = async (index) => {
    const defaultImage = DEFAULT_HERO_IMAGES[index] || { url: '', label: `Hero ${index + 1}` };
    const next = heroImages.map((img, idx) => (idx === index ? defaultImage : img));
    await persistHeroImages(next);
  };

  const addHeroImage = async () => {
    if (heroImages.length >= 5) return;
    const next = [
      ...heroImages,
      {
        url: '',
        label: `Hero ${heroImages.length + 1}`,
      },
    ];
    await persistHeroImages(next);
  };

  const removeHeroImage = async (index) => {
    if (heroImages.length <= 1) return;
    const next = heroImages.filter((_, idx) => idx !== index);
    await persistHeroImages(next);
  };

  const reorderHeroImages = async (fromIndex, toIndex) => {
    if (fromIndex === toIndex || fromIndex == null || toIndex == null) return;
    const next = [...heroImages];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    await persistHeroImages(next);
  };

  const STATUS_COLORS = {
    new: { bg: '#e8f5e9', color: '#2e7d32' },
    preparing: { bg: '#fff3e0', color: '#e65100' },
    ready: { bg: '#e3f2fd', color: '#1565c0' },
    collected: { bg: '#f0f0f0', color: '#555' },
  };
  const ORDER_FILTER_STATUSES = ['new', 'preparing', 'ready', 'collected'];

  return (
    <div
      className="admin-db-root"
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: '#f5f7f8',
      }}
      role="dialog"
      aria-label="Staff dashboard"
    >
      <div
        className="admin-db-header"
        style={{
        background: 'var(--dark)',
        color: 'white',
        padding: '0 2rem',
        minHeight: 64,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexShrink: 0,
        gap: '0.75rem',
      }}
      >
        <div className="admin-db-header-brand" style={{ display: 'flex', alignItems: 'center', gap: '1rem', minWidth: 0 }}>
          <span style={{ fontSize: '1.3rem' }}>🍜</span>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', fontWeight: 700, margin: 0 }}>
            Thai Express — Staff Dashboard
          </h1>
          {loggedIn && (
            <span
              className="live-pill-hide-xs"
              style={{
              background: 'rgba(26,122,138,0.3)',
              border: '1px solid rgba(26,122,138,0.5)',
              color: 'var(--teal-light)',
              fontSize: '.7rem', fontWeight: 700,
              padding: '.2rem .65rem', borderRadius: 999,
              textTransform: 'uppercase', letterSpacing: '.07em',
            }}
            >
              Live
            </span>
          )}
        </div>
        <div className="admin-db-header-actions" style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexShrink: 0 }}>
          {loggedIn && (
            <span
              style={{
                background:
                  dbHealthy == null
                    ? 'rgba(255,255,255,0.08)'
                    : dbHealthy
                      ? 'rgba(24, 165, 80, 0.22)'
                      : 'rgba(226, 75, 75, 0.22)',
                border:
                  dbHealthy == null
                    ? '1px solid rgba(255,255,255,0.15)'
                    : dbHealthy
                      ? '1px solid rgba(24, 165, 80, 0.48)'
                      : '1px solid rgba(226, 75, 75, 0.55)',
                color:
                  dbHealthy == null
                    ? 'rgba(255,255,255,0.8)'
                    : dbHealthy
                      ? '#b9ffd5'
                      : '#ffd1d1',
                padding: '.35rem .7rem',
                borderRadius: 999,
                fontSize: '.72rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '.06em',
              }}
            >
              {dbHealthy == null ? 'DB checking' : dbHealthy ? 'DB connected' : 'DB offline'}
            </span>
          )}
          {loggedIn && (
            <button
              type="button"
              onClick={() => { setLoggedIn(false); setPassword(''); }}
              style={{
                background: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.15)',
                color: 'rgba(255,255,255,0.7)',
                padding: '.4rem 1rem',
                borderRadius: 8, fontSize: '.82rem',
              }}
            >
              Log Out
            </button>
          )}
          <button
            type="button"
            onClick={onExit}
            style={{
              background: 'var(--red)',
              border: 'none',
              color: 'white',
              padding: '.45rem 1.1rem',
              borderRadius: 8, fontSize: '.85rem', fontWeight: 600,
            }}
          >
            Back to site
          </button>
        </div>
      </div>

      {!loggedIn ? (
        <div style={{
          flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'linear-gradient(135deg, var(--teal-deeper), var(--dark))',
        }}
        >
          <div style={{
            background: 'white',
            borderRadius: 'var(--radius-lg)',
            padding: '2.5rem',
            width: 380,
            maxWidth: '92vw',
            boxShadow: 'var(--shadow-hover)',
          }}
          >
            <div style={{ textAlign: 'center', marginBottom: '1.75rem' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '.75rem' }}>🔒</div>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', color: 'var(--dark)' }}>Staff Login</h2>
              <p style={{ color: 'var(--gray)', fontSize: '.85rem', marginTop: '.4rem' }}>Thai Express Seychelles</p>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="al-user">Username</label>
              <input id="al-user" className="form-input" value="admin" readOnly style={{ background: '#f5f5f5' }} />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="al-pass">Password</label>
              <input
                id="al-pass"
                className="form-input"
                type="password"
                placeholder="Enter password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                autoFocus
              />
            </div>
            {loginError && (
              <p style={{ color: 'var(--red)', fontSize: '.82rem', marginBottom: '.75rem' }}>{loginError}</p>
            )}
            <button
              type="button"
              className="btn btn-teal"
              style={{ width: '100%', justifyContent: 'center', padding: '.9rem' }}
              onClick={handleLogin}
            >
              Login
            </button>
            <p style={{ textAlign: 'center', color: 'var(--gray)', fontSize: '.78rem', marginTop: '1rem' }}>
              Demo password: <code style={{ background: '#eee', padding: '2px 6px', borderRadius: 4 }}>admin2025</code>
            </p>
          </div>
        </div>
      ) : (
        <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <div
            className="admin-db-tabs"
            style={{
            background: 'white',
            borderBottom: '2px solid var(--border)',
            padding: '0 clamp(0.75rem, 3vw, 2rem)',
            display: 'flex',
            gap: 0,
            flexShrink: 0,
            flexWrap: 'nowrap',
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
          }}
          >
            {TABS.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTab(t)}
                style={{
                  background: 'none',
                  border: 'none',
                  borderBottom: `2px solid ${tab === t ? 'var(--teal)' : 'transparent'}`,
                  marginBottom: -2,
                  padding: '1rem 1.5rem',
                  color: tab === t ? 'var(--teal)' : 'var(--gray)',
                  fontFamily: 'var(--font-body)',
                  fontWeight: tab === t ? 700 : 500,
                  fontSize: '.9rem',
                  transition: 'all .18s',
                }}
              >
                {t}
                {t === 'Orders' && orders.filter((o) => o.status === 'new').length > 0 && (
                  <span style={{
                    marginLeft: 8,
                    background: 'var(--red)',
                    color: 'white',
                    borderRadius: 999,
                    fontSize: '.68rem',
                    fontWeight: 800,
                    padding: '1px 7px',
                  }}
                  >
                    {orders.filter((o) => o.status === 'new').length}
                  </span>
                )}
              </button>
            ))}
          </div>

          <div className="admin-db-main" style={{ padding: 'clamp(1rem, 4vw, 2rem)', maxWidth: 1100, width: '100%', margin: '0 auto', flex: 1, boxSizing: 'border-box' }}>
            {loading && (
              <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--gray)' }}>Loading...</div>
            )}

            {!loading && tab === 'Overview' && (
              <>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '1rem',
                  marginBottom: '2rem',
                }}
                >
                  <div style={{ background: 'white', padding: '1.25rem', borderRadius: 'var(--radius)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)' }}>
                    <div style={{ fontSize: '.72rem', textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--gray)' }}>Today</div>
                    <div style={{ fontSize: '1.75rem', fontWeight: 800, color: 'var(--teal-dark)', fontFamily: 'var(--font-display)' }}>
                      {formatPrice(todaySales?.total ?? 0)}
                    </div>
                    <div style={{ fontSize: '.8rem', color: 'var(--gray)' }}>{todaySales?.count ?? 0} orders</div>
                  </div>
                  <div style={{ background: 'white', padding: '1.25rem', borderRadius: 'var(--radius)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)' }}>
                    <div style={{ fontSize: '.72rem', textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--gray)' }}>All-time revenue</div>
                    <div style={{ fontSize: '1.75rem', fontWeight: 800, color: 'var(--dark)', fontFamily: 'var(--font-display)' }}>
                      {formatPrice(totalRevenueAllTime)}
                    </div>
                    <div style={{ fontSize: '.8rem', color: 'var(--gray)' }}>{orders.length} orders total</div>
                  </div>
                  <div style={{ background: 'white', padding: '1.25rem', borderRadius: 'var(--radius)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)' }}>
                    <div style={{ fontSize: '.72rem', textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--gray)' }}>Menu items</div>
                    <div style={{ fontSize: '1.75rem', fontWeight: 800, color: 'var(--dark)', fontFamily: 'var(--font-display)' }}>
                      {menuItems.length}
                    </div>
                    <div style={{ fontSize: '.8rem', color: 'var(--gray)' }}>Including hidden</div>
                  </div>
                </div>

                <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.3rem', color: 'var(--dark)', marginBottom: '1rem' }}>
                  Sales by day (last 14 days)
                </h3>
                <div style={{ background: 'white', borderRadius: 'var(--radius)', border: '1px solid var(--border)', padding: '1.25rem', marginBottom: '2.5rem', boxShadow: 'var(--shadow-sm)' }}>
                  {salesRows.map((row) => (
                    <div
                      key={row.dateKey}
                      className="admin-db-sales-row"
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '100px 1fr 80px',
                        alignItems: 'center',
                        gap: '1rem',
                        marginBottom: '.65rem',
                      }}
                    >
                      <span style={{ fontSize: '.82rem', color: 'var(--gray)' }}>
                        {row.date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                      </span>
                      <div style={{ height: 10, background: 'var(--cream)', borderRadius: 999, overflow: 'hidden' }}>
                        <div style={{
                          height: '100%',
                          width: `${(row.total / salesMax) * 100}%`,
                          background: 'linear-gradient(90deg, var(--teal), var(--teal-light))',
                          borderRadius: 999,
                          minWidth: row.total > 0 ? 4 : 0,
                          transition: 'width .3s ease',
                        }}
                        />
                      </div>
                      <span style={{ fontSize: '.82rem', fontWeight: 700, color: 'var(--teal-dark)', textAlign: 'right' }}>
                        {row.total > 0 ? formatPrice(row.total) : '—'}
                      </span>
                    </div>
                  ))}
                </div>

                <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.3rem', color: 'var(--dark)', marginBottom: '1rem' }}>
                  Inventory tracker
                </h3>
                <p style={{ color: 'var(--gray)', fontSize: '.85rem', marginBottom: '1rem', maxWidth: 640 }}>
                  Track approximate stock at the kiosk. Leave blank for items you do not count. Guests do not see these numbers.
                </p>
                <div className="admin-db-inv-wrap" style={{ background: 'white', borderRadius: 'var(--radius)', border: '1px solid var(--border)', overflow: 'hidden', boxShadow: 'var(--shadow-sm)' }}>
                  <div
                    className="admin-db-inv-grid admin-db-inv-head"
                    style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 120px 100px',
                    gap: '1rem',
                    padding: '.75rem 1rem',
                    background: 'var(--cream)',
                    fontSize: '.72rem',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '.06em',
                    color: 'var(--gray)',
                  }}
                  >
                    <span>Item</span>
                    <span>Category</span>
                    <span style={{ textAlign: 'right' }}>Units</span>
                  </div>
                  {menuItems.map((item) => (
                    <div
                      key={item.id}
                      className="admin-db-inv-grid"
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 120px 100px',
                        gap: '1rem',
                        padding: '.65rem 1rem',
                        borderTop: '1px solid var(--border)',
                        alignItems: 'center',
                        fontSize: '.88rem',
                      }}
                    >
                      <span style={{ fontWeight: 600, color: 'var(--dark)' }}>{item.name}</span>
                      <span style={{ color: 'var(--gray)' }}>{item.category}</span>
                      <input
                        type="number"
                        min={0}
                        className="form-input"
                        style={{ padding: '.35rem .5rem', fontSize: '.85rem', textAlign: 'right' }}
                        placeholder="—"
                        value={item.stockUnits == null ? '' : item.stockUnits}
                        onChange={(e) => updateStock(item.id, e.target.value)}
                      />
                    </div>
                  ))}
                </div>
              </>
            )}

            {!loading && tab === 'Business' && (
              <div>
                <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.3rem', color: 'var(--dark)', margin: '0 0 .5rem' }}>
                  Business &amp; checkout settings
                </h2>
                <p style={{ color: 'var(--gray)', fontSize: '.9rem', marginBottom: '1.25rem', maxWidth: 720, lineHeight: 1.6 }}>
                  Currency, tax, and receipt copy used on the public site, cart, and this dashboard. Menu prices are stored as numbers in the currency you set below.
                </p>
                {businessSaveMsg ? (
                  <p style={{
                    color: businessSaveMsg === 'Saved.' ? 'var(--teal-dark)' : 'var(--red)',
                    fontSize: '.88rem',
                    marginBottom: '1rem',
                  }}
                  >
                    {businessSaveMsg}
                  </p>
                ) : null}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                  gap: '1rem',
                  marginBottom: '1rem',
                }}
                >
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label" htmlFor="biz-code">Currency code (ISO)</label>
                    <input
                      id="biz-code"
                      className="form-input"
                      value={businessDraft.currencyCode}
                      onChange={(e) => setBusinessDraft({ ...businessDraft, currencyCode: e.target.value })}
                      placeholder="SCR"
                      autoComplete="off"
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label" htmlFor="biz-sym">Currency symbol</label>
                    <input
                      id="biz-sym"
                      className="form-input"
                      value={businessDraft.currencySymbol}
                      onChange={(e) => setBusinessDraft({ ...businessDraft, currencySymbol: e.target.value })}
                      placeholder="SCR"
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label" htmlFor="biz-pos">Symbol position</label>
                    <select
                      id="biz-pos"
                      className="form-select"
                      value={businessDraft.symbolPosition}
                      onChange={(e) => setBusinessDraft({ ...businessDraft, symbolPosition: e.target.value })}
                    >
                      <option value="before">Before amount</option>
                      <option value="after">After amount</option>
                    </select>
                  </div>
                  <label style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: '.5rem', cursor: 'pointer', fontSize: '.9rem', color: 'var(--dark)', marginBottom: 0 }}>
                    <input
                      type="checkbox"
                      checked={businessDraft.currencyHasDecimals !== false}
                      onChange={(e) => setBusinessDraft({ ...businessDraft, currencyHasDecimals: e.target.checked })}
                    />
                    Currency has decimal units (e.g. cents)
                  </label>
                  <div className="form-group" style={{ marginBottom: 0, opacity: businessDraft.currencyHasDecimals !== false ? 1 : 0.4 }}>
                    <label className="form-label" htmlFor="biz-dp">Decimal places</label>
                    <input
                      id="biz-dp"
                      className="form-input"
                      type="number"
                      min={0}
                      max={4}
                      value={businessDraft.decimalPlaces}
                      disabled={businessDraft.currencyHasDecimals === false}
                      onChange={(e) => setBusinessDraft({ ...businessDraft, decimalPlaces: Number(e.target.value) })}
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label" htmlFor="biz-thousands">Thousands separator</label>
                    <input
                      id="biz-thousands"
                      className="form-input"
                      value={businessDraft.thousandsSeparator}
                      onChange={(e) => setBusinessDraft({ ...businessDraft, thousandsSeparator: e.target.value })}
                      placeholder=","
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0, opacity: businessDraft.currencyHasDecimals !== false ? 1 : 0.4 }}>
                    <label className="form-label" htmlFor="biz-dec">Decimal separator</label>
                    <input
                      id="biz-dec"
                      className="form-input"
                      value={businessDraft.decimalSeparator}
                      disabled={businessDraft.currencyHasDecimals === false}
                      onChange={(e) => setBusinessDraft({ ...businessDraft, decimalSeparator: e.target.value })}
                      placeholder="."
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label" htmlFor="biz-vat">VAT / GST rate (%)</label>
                    <input
                      id="biz-vat"
                      className="form-input"
                      type="number"
                      min={0}
                      max={100}
                      step={0.01}
                      value={businessDraft.vatRatePercent}
                      onChange={(e) => setBusinessDraft({ ...businessDraft, vatRatePercent: Number(e.target.value) })}
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label" htmlFor="biz-svc">Service charge (%)</label>
                    <input
                      id="biz-svc"
                      className="form-input"
                      type="number"
                      min={0}
                      max={100}
                      step={0.01}
                      value={businessDraft.serviceChargePercent}
                      onChange={(e) => setBusinessDraft({ ...businessDraft, serviceChargePercent: Number(e.target.value) })}
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label" htmlFor="biz-min">Minimum order amount</label>
                    <input
                      id="biz-min"
                      className="form-input"
                      type="number"
                      min={0}
                      step={1}
                      value={businessDraft.minimumOrderAmount}
                      onChange={(e) => setBusinessDraft({ ...businessDraft, minimumOrderAmount: Number(e.target.value) })}
                    />
                  </div>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '.5rem', marginBottom: '1rem', cursor: 'pointer', fontSize: '.9rem', color: 'var(--dark)' }}>
                  <input
                    type="checkbox"
                    checked={businessDraft.pricesIncludeVat}
                    onChange={(e) => setBusinessDraft({ ...businessDraft, pricesIncludeVat: e.target.checked })}
                  />
                  Menu prices are VAT-inclusive
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label" htmlFor="biz-tin">Tax / VAT registration no.</label>
                    <input
                      id="biz-tin"
                      className="form-input"
                      value={businessDraft.taxRegistrationNumber}
                      onChange={(e) => setBusinessDraft({ ...businessDraft, taxRegistrationNumber: e.target.value })}
                      placeholder="Optional"
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label" htmlFor="biz-legal">Legal business name (receipts)</label>
                    <input
                      id="biz-legal"
                      className="form-input"
                      value={businessDraft.legalBusinessName}
                      onChange={(e) => setBusinessDraft({ ...businessDraft, legalBusinessName: e.target.value })}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="biz-foot">Receipt footer line</label>
                  <textarea
                    id="biz-foot"
                    className="form-textarea"
                    rows={2}
                    value={businessDraft.receiptFooterLine}
                    onChange={(e) => setBusinessDraft({ ...businessDraft, receiptFooterLine: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="biz-pay">Payment note (checkout)</label>
                  <textarea
                    id="biz-pay"
                    className="form-textarea"
                    rows={2}
                    value={businessDraft.paymentNote}
                    onChange={(e) => setBusinessDraft({ ...businessDraft, paymentNote: e.target.value })}
                  />
                </div>
                <p style={{ fontSize: '.82rem', color: 'var(--gray)', marginBottom: '1rem', lineHeight: 1.55 }}>
                  Preview: <strong>{formatPrice(1234.5)}</strong>
                </p>
                <button type="button" className="btn btn-teal" onClick={saveBusinessSettings}>
                  Save business settings
                </button>
              </div>
            )}

            {!loading && tab === 'Menu' && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
                  <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.3rem', color: 'var(--dark)', margin: 0 }}>
                    Menu management
                  </h2>
                  <button
                    type="button"
                    className="btn btn-teal"
                    onClick={() => setShowAddForm((s) => !s)}
                  >
                    {showAddForm ? 'Cancel' : '+ Add menu item'}
                  </button>
                </div>

                {showAddForm && (
                  <div style={{
                    background: 'white',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius)',
                    padding: '1.5rem',
                    marginBottom: '1.5rem',
                    boxShadow: 'var(--shadow-sm)',
                  }}
                  >
                    <h3 style={{ fontSize: '1rem', marginBottom: '1rem', color: 'var(--dark)' }}>New dish</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                      <div className="form-group">
                        <label className="form-label" htmlFor="ni-name">Name</label>
                        <input id="ni-name" className="form-input" value={newItem.name} onChange={(e) => setNewItem({ ...newItem, name: e.target.value })} />
                      </div>
                      <div className="form-group">
                        <label className="form-label" htmlFor="ni-price">Price ({businessDraft.currencyCode || '—'})</label>
                        <input id="ni-price" className="form-input" type="number" min={0} value={newItem.price} onChange={(e) => setNewItem({ ...newItem, price: e.target.value })} />
                      </div>
                      <div className="form-group">
                        <label className="form-label" htmlFor="ni-cat">Category</label>
                        <input
                          id="ni-cat"
                          className="form-input"
                          list="menu-category-options"
                          value={newItem.category}
                          onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
                          placeholder="e.g. Noodles, Curries"
                        />
                        <datalist id="menu-category-options">
                          {categoryOptions.map((c) => (
                            <option key={c} value={c} />
                          ))}
                        </datalist>
                      </div>
                      <div className="form-group">
                        <label className="form-label" htmlFor="ni-emoji">Emoji</label>
                        <input id="ni-emoji" className="form-input" value={newItem.emoji} onChange={(e) => setNewItem({ ...newItem, emoji: e.target.value })} />
                      </div>
                      <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                        <label className="form-label" htmlFor="ni-desc">Description</label>
                        <textarea id="ni-desc" className="form-textarea" value={newItem.desc} onChange={(e) => setNewItem({ ...newItem, desc: e.target.value })} rows={2} />
                      </div>
                      <div className="form-group">
                        <label className="form-label" htmlFor="ni-prep">Prep (min)</label>
                        <input id="ni-prep" className="form-input" type="number" min={1} value={newItem.prepMinutes} onChange={(e) => setNewItem({ ...newItem, prepMinutes: e.target.value })} />
                      </div>
                      <div className="form-group">
                        <label className="form-label" htmlFor="ni-spice">Spice level (0–2)</label>
                        <input id="ni-spice" className="form-input" type="number" min={0} max={2} value={newItem.spiceLevel} onChange={(e) => setNewItem({ ...newItem, spiceLevel: e.target.value })} />
                      </div>
                      <div className="form-group">
                        <label className="form-label" htmlFor="ni-stock">Stock units (optional)</label>
                        <input id="ni-stock" className="form-input" type="number" min={0} placeholder="Optional" value={newItem.stockUnits} onChange={(e) => setNewItem({ ...newItem, stockUnits: e.target.value })} />
                      </div>
                      <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                        <AdminImagePicker
                          previewUrl={
                            newImageFile
                            || (/^https?:\/\//i.test(newItem.imageUrl.trim()) ? newItem.imageUrl.trim() : null)
                          }
                          fallbackEmoji={newItem.emoji || '🍽'}
                          urlValue={newItem.imageUrl}
                          onUrlChange={(v) => {
                            setNewImageFile(null);
                            setNewItem((ni) => ({ ...ni, imageUrl: v }));
                          }}
                          onUrlBlur={() => {}}
                          onFileSelect={(file) => {
                            if (!file) return;
                            const reader = new FileReader();
                            reader.onload = () => {
                              setNewImageFile(reader.result);
                              setNewItem((ni) => ({ ...ni, imageUrl: '' }));
                            };
                            reader.readAsDataURL(file);
                          }}
                          onClearImage={() => {
                            setNewImageFile(null);
                            setNewItem((ni) => ({ ...ni, imageUrl: '' }));
                          }}
                        />
                      </div>
                    </div>
                    <button type="button" className="btn btn-teal" style={{ marginTop: '1rem' }} onClick={handleAddItem}>
                      Save dish
                    </button>
                  </div>
                )}

                <p style={{ color: 'var(--gray)', fontSize: '.85rem', margin: '0 0 1rem', lineHeight: 1.5 }}>
                  Each dish is one card: toggles and price at the top; image URL, file upload, and live thumbnail below.
                </p>

                {menuItems.map((item) => (
                  <AdminMenuItemCard
                    key={item.id}
                    item={item}
                    categoryOptions={categoryOptions}
                    onUpdateCategory={updateCategory}
                    onToggleField={toggleField}
                    onUpdateImageUrl={updateImageUrl}
                    onUpdateImageFile={updateImageFile}
                    onDelete={deleteMenuItem}
                  />
                ))}
              </>
            )}

            {!loading && tab === 'Orders' && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
                  <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.3rem', color: 'var(--dark)', margin: 0 }}>Orders</h2>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: '.45rem', color: 'var(--gray)', fontSize: '.82rem' }}>
                      <input
                        type="checkbox"
                        checked={showTodayOnlyOrders}
                        onChange={(e) => setShowTodayOnlyOrders(e.target.checked)}
                      />
                      Today only
                    </label>
                    <p style={{ color: 'var(--gray)', fontSize: '.85rem', margin: 0 }}>
                      {filteredOrders.length} shown, {orders.filter((o) => o.status === 'new').length} new
                    </p>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
                  {ORDER_FILTER_STATUSES.map((status) => {
                    const active = visibleOrderStatuses.includes(status);
                    const statusColor = STATUS_COLORS[status] || STATUS_COLORS.new;
                    return (
                      <button
                        key={`filter-${status}`}
                        type="button"
                        onClick={() => {
                          setVisibleOrderStatuses((prev) => {
                            if (prev.includes(status)) {
                              if (prev.length === 1) return prev;
                              return prev.filter((s) => s !== status);
                            }
                            return [...prev, status];
                          });
                        }}
                        style={{
                          minHeight: 40,
                          minWidth: 92,
                          borderRadius: 999,
                          border: '1.5px solid',
                          borderColor: active ? statusColor.color : 'var(--border)',
                          background: active ? statusColor.bg : 'white',
                          color: active ? statusColor.color : 'var(--gray)',
                          fontWeight: 700,
                          fontSize: '.78rem',
                          textTransform: 'capitalize',
                          padding: '.45rem .85rem',
                          cursor: 'pointer',
                          transition: 'all .15s',
                        }}
                        aria-pressed={active}
                      >
                        {status}
                      </button>
                    );
                  })}
                </div>

                {filteredOrders.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '4rem', color: 'var(--gray)' }}>
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📋</div>
                    <p>
                      {showTodayOnlyOrders
                        ? 'No orders scheduled for today.'
                        : 'No orders yet. They will appear here as customers order online.'}
                    </p>
                  </div>
                ) : (
                  groupedOrdersByPickup.map((group) => (
                    <div key={group.timeLabel} style={{ marginBottom: '1.25rem' }}>
                      <div style={{
                        fontSize: '.78rem',
                        textTransform: 'uppercase',
                        letterSpacing: '.08em',
                        fontWeight: 700,
                        color: 'var(--teal-dark)',
                        marginBottom: '.6rem',
                      }}
                      >
                        Pickup {group.timeLabel}
                      </div>
                      {group.list.map((order) => {
                        const sc = STATUS_COLORS[order.status] || STATUS_COLORS.new;
                        return (
                          <div
                            key={order.id}
                            style={{
                              background: 'white',
                              borderRadius: 'var(--radius)',
                              padding: '1.25rem 1.5rem',
                              marginBottom: '.75rem',
                              boxShadow: 'var(--shadow-sm)',
                              border: '1px solid var(--border)',
                              borderLeft: order.status === 'new' ? '4px solid var(--red)' : '4px solid var(--border)',
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem', flexWrap: 'wrap', gap: '.75rem' }}>
                              <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '.75rem', marginBottom: '.3rem' }}>
                                  <span style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--teal-dark)', fontSize: '.95rem' }}>
                                    {order.orderId}
                                  </span>
                                  <span style={{
                                    background: sc.bg, color: sc.color,
                                    fontSize: '.72rem', fontWeight: 700,
                                    padding: '3px 10px', borderRadius: 999,
                                    textTransform: 'uppercase', letterSpacing: '.06em',
                                  }}
                                  >
                                    {order.status}
                                  </span>
                                </div>
                                <div style={{ fontSize: '.85rem', color: 'var(--dark)', fontWeight: 600 }}>
                                  {order.name}, {order.phone}
                                </div>
                                <div style={{ fontSize: '.8rem', color: 'var(--gray)', marginTop: 2 }}>
                                  Pickup: <strong>{order.pickupLabel || 'ASAP'}</strong>
                                  , ordered at {new Date(order.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </div>
                              </div>
                              <div style={{ textAlign: 'right' }}>
                                <div style={{ fontWeight: 800, fontSize: '1.1rem', color: 'var(--teal-dark)' }}>{formatPrice(order.total)}</div>
                                <div style={{ fontSize: '.75rem', color: 'var(--gray)' }}>Paid online</div>
                                <button
                                  type="button"
                                  onClick={() => printOrderLabel(order)}
                                  title="Print label"
                                  aria-label={`Print label for ${order.orderId || order.id}`}
                                  style={{
                                    marginTop: '.55rem',
                                    width: 46,
                                    height: 46,
                                    borderRadius: 12,
                                    border: '1.5px solid rgba(26,122,138,0.35)',
                                    background: 'rgba(26,122,138,0.08)',
                                    color: 'var(--teal-dark)',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                  }}
                                >
                                  <span className="material-symbols-outlined" style={{ fontSize: 24, lineHeight: 1 }}>print</span>
                                </button>
                              </div>
                            </div>

                            <div style={{ marginBottom: '1rem' }}>
                              {order.items.map((it, i) => (
                                <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'var(--cream)', borderRadius: 999, padding: '3px 10px', marginRight: 6, marginBottom: 4, fontSize: '.8rem', color: 'var(--dark)' }}>
                                  {it.emoji} {it.name} x{it.qty}
                                </span>
                              ))}
                            </div>

                            {order.notes && (
                              <div style={{ fontSize: '.82rem', color: 'var(--gray)', background: 'var(--cream)', padding: '.5rem .85rem', borderRadius: 8, marginBottom: '.75rem' }}>
                                Note: {order.notes}
                              </div>
                            )}

                            <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap' }}>
                              {['new', 'preparing', 'ready', 'collected'].map((s) => (
                                <button
                                  key={s}
                                  type="button"
                                  onClick={() => updateOrderStatus(order.id, s)}
                                  style={{
                                    padding: '.35rem .9rem',
                                    borderRadius: 999,
                                    border: '1.5px solid',
                                    borderColor: order.status === s ? STATUS_COLORS[s].color : 'var(--border)',
                                    background: order.status === s ? STATUS_COLORS[s].bg : 'none',
                                    color: order.status === s ? STATUS_COLORS[s].color : 'var(--gray)',
                                    fontSize: '.75rem',
                                    fontWeight: 700,
                                    textTransform: 'capitalize',
                                    transition: 'all .18s',
                                  }}
                                >
                                  {s}
                                </button>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))
                )}
              </>
            )}

            {!loading && tab === 'Hero' && (
              <>
                <div style={{ marginBottom: '1.5rem' }}>
                  <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.3rem', color: 'var(--dark)', margin: '0 0 .6rem' }}>
                    Hero pictures
                  </h2>
                  <p style={{ color: 'var(--gray)', fontSize: '.9rem', margin: 0, lineHeight: 1.6, maxWidth: 760 }}>
                    Upload up to five slideshow photos shown on the homepage hero. Drag and drop to reorder. Save happens automatically after each change.
                  </p>
                </div>

                <div style={{
                  background: '#f3fafb',
                  border: '1px solid rgba(26,122,138,0.2)',
                  borderRadius: 'var(--radius)',
                  padding: '1rem 1.15rem',
                  marginBottom: '1.25rem',
                }}
                >
                  <h3 style={{ margin: '0 0 .4rem', fontSize: '.92rem', color: 'var(--teal-dark)' }}>Recommended image specs</h3>
                  <p style={{ margin: '0 0 .4rem', fontSize: '.82rem', color: 'var(--gray)', lineHeight: 1.55 }}>
                    <strong>Aspect ratio:</strong> 16:9 (landscape), <strong>minimum size:</strong> 1920 x 1080, <strong>ideal size:</strong> 2560 x 1440.
                  </p>
                  <p style={{ margin: 0, fontSize: '.82rem', color: 'var(--gray)', lineHeight: 1.55 }}>
                    Use JPG/WebP under 1.5 MB each for fast load. Keep key food subject centered; top/bottom edges may crop on some devices.
                  </p>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '.9rem', gap: '1rem', flexWrap: 'wrap' }}>
                  <p style={{ margin: 0, fontSize: '.82rem', color: 'var(--gray)' }}>
                    {heroImages.length} / 5 hero images
                  </p>
                  <button
                    type="button"
                    className="btn btn-teal"
                    onClick={addHeroImage}
                    disabled={heroImages.length >= 5}
                    style={{
                      opacity: heroImages.length >= 5 ? 0.45 : 1,
                      cursor: heroImages.length >= 5 ? 'not-allowed' : 'pointer',
                    }}
                  >
                    + Add hero image
                  </button>
                </div>

                {heroImages.map((img, index) => (
                  <div
                    key={`hero-slot-${index + 1}`}
                    draggable
                    onDragStart={() => setDragIndex(index)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={async () => {
                      await reorderHeroImages(dragIndex, index);
                      setDragIndex(null);
                    }}
                    onDragEnd={() => setDragIndex(null)}
                    style={{
                      background: 'white',
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--radius)',
                      boxShadow: 'var(--shadow-sm)',
                      padding: '1.1rem',
                      marginBottom: '1rem',
                      cursor: 'move',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '.85rem', gap: '1rem' }}>
                      <h3 style={{ margin: 0, fontSize: '1rem', color: 'var(--dark)' }}>Hero image {index + 1}</h3>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
                        <button
                          type="button"
                          onClick={() => resetHeroImage(index)}
                          style={{
                            border: '1px solid var(--border)',
                            borderRadius: 8,
                            background: '#fafafa',
                            color: 'var(--gray)',
                            fontSize: '.78rem',
                            padding: '.35rem .7rem',
                            cursor: 'pointer',
                          }}
                        >
                          Reset
                        </button>
                        <button
                          type="button"
                          onClick={() => removeHeroImage(index)}
                          disabled={heroImages.length <= 1}
                          style={{
                            border: '1px solid rgba(200,72,72,0.28)',
                            borderRadius: 8,
                            background: 'rgba(200,72,72,0.08)',
                            color: 'var(--red)',
                            fontSize: '.78rem',
                            padding: '.35rem .7rem',
                            cursor: heroImages.length <= 1 ? 'not-allowed' : 'pointer',
                            opacity: heroImages.length <= 1 ? 0.45 : 1,
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    </div>

                    <div className="form-group" style={{ marginBottom: '.8rem' }}>
                      <label className="form-label" htmlFor={`hero-label-${index}`}>Label</label>
                      <input
                        id={`hero-label-${index}`}
                        className="form-input"
                        value={img.label}
                        onChange={(e) => updateHeroLabel(index, e.target.value)}
                        placeholder={`Hero ${index + 1}`}
                      />
                    </div>

                    <AdminImagePicker
                      previewUrl={img.url || null}
                      fallbackEmoji="📸"
                      urlValue={img.url || ''}
                      onUrlChange={(v) => updateHeroUrl(index, v)}
                      onUrlBlur={() => {}}
                      onFileSelect={(file) => updateHeroFile(index, file)}
                      onClearImage={() => updateHeroUrl(index, '')}
                    />
                  </div>
                ))}
              </>
            )}

            {!loading && tab === 'Reviews' && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
                  <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.3rem', color: 'var(--dark)', margin: 0 }}>Guest reviews</h2>
                  <p style={{ color: 'var(--gray)', fontSize: '.85rem' }}>{reviews.length} reviews</p>
                </div>

                {reviews.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '4rem', color: 'var(--gray)' }}>
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>💬</div>
                    <p>No reviews yet.</p>
                  </div>
                ) : (
                  [...reviews].reverse().map((r) => (
                    <div
                      key={r.id}
                      style={{
                        background: 'white',
                        borderRadius: 'var(--radius-sm)',
                        padding: '1.1rem 1.25rem',
                        marginBottom: '.75rem',
                        boxShadow: 'var(--shadow-sm)',
                        border: '1px solid var(--border)',
                        display: 'flex',
                        gap: '1rem',
                        alignItems: 'flex-start',
                      }}
                    >
                      <div style={{ minWidth: 40, textAlign: 'center' }}>
                        <div style={{ fontSize: '1.1rem', color: 'var(--gold)' }}>{'★'.repeat(r.rating)}</div>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, color: 'var(--dark)', fontSize: '.9rem', marginBottom: '.25rem' }}>
                          {r.name}
                          {r.contact && (
                            <span style={{ color: 'var(--gray)', fontWeight: 400, fontSize: '.8rem', marginLeft: 8 }}>{r.contact}</span>
                          )}
                        </div>
                        <div style={{ color: 'var(--gray)', fontSize: '.88rem', lineHeight: 1.6, marginBottom: '.4rem' }}>
                          &ldquo;{r.text}&rdquo;
                        </div>
                        <div style={{ fontSize: '.75rem', color: 'var(--gray-light)' }}>
                          {new Date(r.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => deleteReview(r.id)}
                        title="Delete review"
                        style={{
                          background: 'none',
                          border: '1px solid var(--border)',
                          borderRadius: 6,
                          padding: '.3rem .65rem',
                          color: 'var(--red)',
                          fontSize: '.8rem',
                          transition: 'all .15s',
                          flexShrink: 0,
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  ))
                )}
              </>
            )}
          </div>
        </div>
      )}
      <style>{`
        @media (max-width: 720px) {
          .admin-db-header {
            flex-wrap: wrap !important;
            padding: 0.65rem 1rem !important;
            height: auto !important;
          }
          .admin-db-header-brand h1 {
            font-size: 1rem !important;
          }
          .admin-db-header-actions {
            width: 100%;
            justify-content: flex-end;
            flex-wrap: wrap;
          }
          .admin-db-tabs button {
            padding: 0.85rem 1rem !important;
            font-size: 0.85rem !important;
          }
          .admin-db-sales-row {
            grid-template-columns: 72px 1fr minmax(4.5rem, 1fr) !important;
            gap: 0.5rem !important;
          }
          .admin-db-inv-grid {
            grid-template-columns: 1fr !important;
          }
          .admin-db-inv-head span:nth-child(2),
          .admin-db-inv-head span:nth-child(3) {
            display: none;
          }
          .admin-db-inv-grid:not(.admin-db-inv-head) span:nth-child(2) {
            font-size: 0.78rem;
            color: var(--gray);
          }
        }
        @media (max-width: 480px) {
          .admin-db-header-brand .live-pill-hide-xs {
            display: none;
          }
        }
      `}</style>
    </div>
  );
}
